// Copyright (c) 2019 Deere & Company

import React, {PropTypes} from 'react';
import {
    getVsrMessage,
    hasVsrErrorForClaimField,
    isResponsibleAccountEditable,
    useAmountIndexToChange
} from '../../../shared/services/claim-shared-helper';

import AmountOfUseField from '../../../shared/components/claim-fields/amount-of-use';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import IncentiveClaim from '@deere/cps-it.warranty-model-factory/lib/claim-incentive';
import LabeledDateInput from '../../../../claim/shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import {fixCursorPosition} from '../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../shared/services/format-pin';
import {isValidClaim} from '../../services/claim-validation-helper';
import {isValidDate} from '../../../../shared/services/build-date';
import moment from 'moment';

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts, '');

}

function resetCustomerProductDetails() {

    this.actions.resetCustomerDetails();
    this.actions.resetCkcCustomerFailure();
    this.actions.resetProductDetails();

}

function handlePinBlur(pin) {

    this.actions.updateIncentiveClaimInputOnBlur('pin', pin);

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin, true);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handleInputBlur(property, value) {

    this.actions.updateIncentiveClaimInputOnBlur(property, value, this.claim);

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updateIncentiveClaimPin(formattedPin);
    this.actions.updateNonTrackedPinModalPin(formattedPin);
    fixCursorPosition(event, formattedPin);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updateIncentiveClaimWorkOrderOne(workOrder);

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updateIncentiveClaimWorkOrderTwo(workOrder);

}

function handleCustomerNameChange(customerName) {

    this.actions.updateIncentiveCustomerName(customerName);

}

function handlecustomerEmailChange(email) {

    this.actions.updateIncentiveEmail(email);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateIncentiveClaimRepairNumber(repairNumber);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? moment.utc(date.target.value).valueOf() : null;

    this.actions.updateIncentiveClaimInputOnBlur(property, modifiedDate, this.claim);

}

function handleRepairDateChange(date) {

    this.actions.updateIncentiveClaimRepairDate(date);

}

function handleFailurePartChange(partNumber) {

    this.actions.updateIncentiveClaimFailurePart(partNumber);

}

function handleUnitOptionChange(key) {

    this.actions.updateIncentiveClaimUnitDropDown(key, this.product.useAmountAllowedUnits);

}

function handleUseAmountChange(amount, index) {

    const indexToChange = useAmountIndexToChange(this.product.complianceApplies, index);

    this.actions.updateIncentiveClaimUseAmount(amount, indexToChange);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}
const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='pin'
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
                <h3>
                    <FormattedMessage id='CUSTOMER_INFORMATION'/>
                </h3>
                <div className='customer-info'>
                    <LabeledInputWithBlur
                        className='customer-name'
                        errorMessage='CUSTOMER_NAME_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'customerName', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.customerName}
                        label='CUSTOMER_NAME'
                        maxLength={36}
                        onBlur={handleInputBlur.bind(props, 'customerName')}
                        onChange={props::handleCustomerNameChange}
                        value={props.claim.customer.customerName}
                        vsrMessage={getVsrMessage(props.claim.errors, 'customerName', props.isEditable)}
                    />
                    <LabeledInput
                        className='email'
                        isEditable={props.isEditable}
                        label='EMAIL'
                        maxLength={241}
                        onChange={props::handlecustomerEmailChange}
                        value={props.claim.customer.email}
                        vsrMessage={getVsrMessage(props.claim.errors, 'email', props.isEditable)}
                    />
                </div>
                <h3>
                    <FormattedMessage id='REFERENCE_INFORMATION'/>
                </h3>
                <div className='reference-info'>

                    <LabeledInputWithBlur
                        className='work-order1'
                        errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.workOrderOne}
                        label='WORK_ORDER_ONE'
                        maxLength={26}
                        onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                        onChange={props::handleWorkOrderOneChange}
                        value={props.claim.referenceInfo.referenceNumber1}
                        vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                    />
                    <LabeledInput
                        className='work-order2'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                        isEditable={props.isEditable}
                        label='WORK_ORDER_TWO'
                        maxLength={12}
                        onChange={props::handleWorkOrderTwoChange}
                        value={props.claim.referenceInfo.referenceNumber2}
                    />

                    <LabeledInput
                        className='repair-number'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                        isEditable={props.isEditable}
                        label='REPAIR_NUMBER'
                        maxLength={13}
                        onChange={props::handleRepairNumberChange}
                        value={props.claim.referenceInfo.repairNumber}
                        vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                    />

                    <LabeledDateInput
                        className='repair-date'
                        date={props.claim.failureInfo.repairDate}
                        errorMessage='SERVICED_DATE_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.repairDate}
                        label='SERVICED'
                        maxDate={moment()}
                        onBlur={handleDateBlur.bind(props, 'repairDate')}
                        onDateChange={props::handleRepairDateChange}
                        vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                    />

                    <LabeledInputWithBlur
                        className='failure-part'
                        errorMessage='FAILED_PART_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.failurePart}
                        label='FAILED_PART'
                        maxLength={18}
                        onBlur={handleInputBlur.bind(props, 'failurePart')}
                        onChange={props::handleFailurePartChange}
                        value={props.claim.failureInfo.failurePartNumber}
                        vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                    />

                    {props.product.useAmountAllowedUnits.length > 0 &&
                        <AmountOfUseField
                            allowedUnits={props.product.useAmountAllowedUnits}
                            className='amount-of-use'
                            complianceApplies={props.product.complianceApplies}
                            errors={props.claim.errors}
                            formValidation={props.formValidation}
                            isEditable={props.isEditable}
                            onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                            onInputChange={props::handleUseAmountChange}
                            onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                            onOptionChange={props::handleUnitOptionChange}
                            useAmounts={props.claim.useAmounts}
                        />
                    }

                </div>
            </div>
            {(props.isEditable && props.claim.showValidate) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim, props.product.useAmountAllowedUnits.length)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(IncentiveClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    isEditable: PropTypes.bool.isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/incentive/components/claim-details-section/claim-details-container.js